import api from './api';
import {
    cloneRequestForStorage,
    normalizeRequestForCard
} from './projections';
import {
    toFileArray,
    uploadFilesSequential
} from '@/utils/fileUploadQueue';

async function refreshRequestAndCounts({ state, dispatch }) {
    const requestId = state.request && state.request.id;
    if (!requestId) {
        return;
    }

    await dispatch('requestCard_fetchRequest', requestId);
    try {
        await dispatch('requests_getCounts', null, { root: true });
    } catch (e) {
        console.error('Ошибка при обновлении счетчиков:', e);
    }
}

function getCustomFieldValues(request) {
    const workflowData = request && request.workflow_data ? request.workflow_data : {};
    const values = workflowData.custom_fields || workflowData.customFields || {};
    return values && typeof values === 'object' && !Array.isArray(values) ? values : {};
}

function isSameJsonValue(left, right) {
    return JSON.stringify(left || {}) === JSON.stringify(right || {});
}

const pendingDraftSaves = new WeakMap();

function assertNoPendingRequestChanges(state, getters) {
    if (state.savingRequest || (getters && getters.hasFormChanges)) {
        throw new Error('Сначала сохраните или отмените изменения заявки.');
    }
}

/**
 * Actions для карточки заявки
 */
export default {
    /**
     * Загрузить заявку со всеми данными
     */
    async requestCard_fetchRequest({ state, commit, dispatch }, requestId) {
        commit('setLoading', true);
        try {
            const [requestData, activity, actionsData] = await Promise.all([
                api.getRequest(requestId),
                api.getRequestActivity(requestId).catch(() => ({ items: [] })),
                api.getAvailableActions(requestId).catch(() => ({ actions: [], permissions: {}, actionHints: [], isAdmin: false, currentUserRole: null }))
            ]);

            const request = normalizeRequestForCard(requestData, actionsData);
            commit('setRequest', request);
            commit('setOriginalRequest', cloneRequestForStorage(request));
            commit('setRequestActivity', (activity && activity.items) || []);
            commit('clearNewAttachments');
            commit('clearRemovedFileIds');

            // Список доступных исполнителей — отдельная API available-executors
            // Грузим, если пользователь может назначать (action assign)
            // ИЛИ может переназначать строки в multi-режиме (canReassignItemExecutor)
            const canAssign = (request.availableActions && request.availableActions.includes('assign')) || false;
            const canReassignItem = !!(request.permissions && request.permissions.canReassignItemExecutor);
            const isManual = request.RequestType && request.RequestType.executor_assignment_manual;
            if ((canAssign || canReassignItem) && isManual) {
                if (state.availableExecutors.length === 0) {
                    try {
                        const result = await api.getAvailableExecutors(requestId);
                        commit('setAvailableExecutors', (result && result.executors) || []);
                    } catch (e) {
                        commit('setAvailableExecutors', []);
                    }
                }
            } else {
                commit('setAvailableExecutors', []);
            }

            // Сбрасываем изменения материалов при загрузке заявки
            commit('clearMaterialsChanges');

            return request;
        } catch (error) {
            console.error('Ошибка при загрузке заявки:', error);
            throw error;
        } finally {
            commit('setLoading', false);
        }
    },

    async requestCard_discardChanges({state, commit, dispatch}) {
        if (state.savingRequest || state.uploadingRequiredAttachment) throw new Error('Дождитесь завершения сохранения');
        clearTimeout(state.autoSaveTimer);
        commit('setAutoSaveTimer', null);
        const requestTypeId = state.request.request_type_id;
        // A failed read must leave the draft intact. Fetch commits only after the request is received.
        await dispatch('requestCard_fetchRequest', state.request.id);
        const draftSave = pendingDraftSaves.get(state);
        if (draftSave) await draftSave.catch(() => {});
        if (requestTypeId) await api.deleteDraftByType(requestTypeId).catch(() => {});
    },

    /**
     * Сохранить заявку
     */
    async requestCard_saveRequest({ state, commit, dispatch, getters }) {
        if (state.savingRequest) throw new Error('Дождитесь завершения сохранения заявки');
        if (state.uploadingRequiredAttachment) {
            throw new Error('Дождитесь загрузки обязательного документа');
        }
        if (!state.request || !state.request.id || !getters.hasFormChanges) {
            return;
        }

        const requestId = state.request.id;
        const requestTypeId = state.request.request_type_id;
        const assertCurrentRequest = () => {
            if (!state.request || state.request.id !== requestId) {
                throw new Error('Открыта другая заявка. Сохранение оставшихся правок остановлено.');
            }
        };
        let savedAny = false;
        let objectSaved = false;
        clearTimeout(state.autoSaveTimer);
        commit('setAutoSaveTimer', null);
        commit('setSavingRequest', true);
        try {
            // Строим payload измененных полей
            const allowedFields = [
                'request_type_id',
                'title',
                'description',
                'priority',
                'deadline',
                'urgency_comment',
                'building_process_id'
            ];

            const changedFields = allowedFields.reduce((acc, field) => {
                if (!Object.prototype.hasOwnProperty.call(state.request, field)) {
                    return acc;
                }
                const currentValue = state.request[field];
                const originalValue = state.originalRequest ? state.originalRequest[field] : undefined;
                if (currentValue !== originalValue) {
                    acc[field] = currentValue;
                }
                return acc;
            }, {});
            const objectChanged = Object.prototype.hasOwnProperty.call(changedFields, 'building_process_id');
            const nextObjectId = changedFields.building_process_id;
            delete changedFields.building_process_id;

            const currentCustomFields = getCustomFieldValues(state.request);
            const originalCustomFields = getCustomFieldValues(state.originalRequest);
            if (!isSameJsonValue(currentCustomFields, originalCustomFields)) {
                changedFields.custom_fields = JSON.parse(JSON.stringify(currentCustomFields));
            }

            const attachmentsToUpload = state.newAttachments.slice();
            const removedFileIds = state.removedFileIds.slice();
            // Сначала сохраняем замену: сбой загрузки не должен удалить старый файл.
            for (const file of attachmentsToUpload) {
                const result = await api.uploadRequestFiles(requestId, [file]);
                savedAny = true;
                assertCurrentRequest();
                commit('mergeUploadedRequestFiles', { requestId, files: result.files });
                commit('removeNewAttachment', state.newAttachments.indexOf(file));
            }
            const requestPayload = { ...changedFields };
            if (removedFileIds.length) {
                requestPayload.remove_file_ids = removedFileIds;
            }

            if (Object.keys(requestPayload).length) {
                const response = await api.updateRequest(requestId, requestPayload);
                savedAny = true;
                assertCurrentRequest();
                commit('acknowledgeRequestFields', {requestId, fields: requestPayload, response});
            }

            // Обновление позиций (не-материалов)
            const currentItems = Array.isArray(state.request.items)
                ? state.request.items.filter(item => item.item_type !== 'material')
                : [];
            const originalItems = Array.isArray(state.originalRequest.items)
                ? state.originalRequest.items.filter(item => item.item_type !== 'material')
                : [];
            const originalMap = new Map(originalItems.map(item => [item.id, item]));
            
            const itemUpdates = currentItems.reduce((acc, item) => {
                const original = originalMap.get(item.id);
                if (!original) {
                    return acc;
                }
                const payload = {};
                if (item.item_name !== original.item_name) payload.item_name = item.item_name;
                if (item.quantity !== original.quantity) payload.quantity = item.quantity;
                if (item.unit !== original.unit) payload.unit = item.unit;
                if (Object.keys(payload).length) {
                    acc.push({ itemId: item.id, payload });
                }
                return acc;
            }, []);

            for (const { itemId, payload } of itemUpdates) {
                await api.updateRequestItem(requestId, itemId, payload);
                savedAny = true;
                assertCurrentRequest();
                commit('acknowledgeRequestItem', {requestId, itemId, payload});
            }

            // Обработка изменений материалов
            if (getters.materialsListHaveChanges) {
                const changes = JSON.parse(JSON.stringify(state.materialsChanges));
                const bulkItems = [...changes.added, ...changes.updated, ...changes.deleted];

                if (bulkItems.length > 0) {
                    const response = await api.bulkUpdateMaterials(requestId, bulkItems);
                    savedAny = true;
                    assertCurrentRequest();
                    commit('acknowledgeMaterials', {requestId, changes, results: response.results});
                }
            }

            // Пересчёт участников может отозвать доступ: объект меняем последним.
            if (objectChanged) {
                const fields = {building_process_id: nextObjectId || null};
                const response = await api.updateRequest(requestId, fields);
                savedAny = true;
                objectSaved = true;
                assertCurrentRequest();
                commit('acknowledgeRequestFields', {requestId, fields, response});
            }

            // Удаляем черновик после успешного сохранения заявки
            if (requestTypeId) {
                try {
                    const draftSave = pendingDraftSaves.get(state);
                    if (draftSave) await draftSave.catch(() => {});
                    await api.deleteDraftByType(requestTypeId);
                } catch (e) {
                    console.error('Ошибка удаления черновика:', e);
                    // Не показываем ошибку пользователю, так как заявка уже сохранена
                }
            }

            if (!state.request || state.request.id !== requestId) return {success: true};
            try {
                await dispatch('requestCard_fetchRequest', requestId);
            } catch (error) {
                if (objectSaved && error.response && [403, 404].includes(error.response.status)) {
                    return {success: true, lostAccess: true};
                }
                return {success: true, refreshFailed: true,
                    message: 'Изменения сохранены, но обновить карточку не удалось. Обновите страницу перед продолжением.'};
            }
            return { success: true };
        } catch (error) {
            if (savedAny) {
                const message = 'Часть изменений уже сохранена. Остальные правки оставлены в форме. ' +
                    ((error.response && error.response.data && error.response.data.message) || error.message);
                error.message = message;
                if (error.response && error.response.data) error.response.data.message = message;
            }
            console.error('Ошибка при сохранении заявки:', error);
            throw error;
        } finally {
            commit('setSavingRequest', false);
        }
    },

    /**
     * Изменить срок выполнения/срочность без общего сохранения заявки.
     */
    async requestCard_updateDeadline({ state, commit }, payload) {
        if (!state.request || !state.request.id) {
            return;
        }

        commit('setSavingRequest', true);
        try {
            const requestPayload = {
                ...(payload || {}),
                expected_updated_at: state.originalRequest && state.originalRequest.updatedAt
            };
            if (state.request.isAdmin === true && state.adminGodModeEnabled === true) {
                requestPayload.god_mode = true;
            }
            const updated = await api.updateRequest(state.request.id, requestPayload);
            commit('mergeDeadlineUpdate', updated);
            const actionsData = await api.getAvailableActions(state.request.id).catch(() => null);
            if (actionsData) commit('mergeDeadlineAccess', normalizeRequestForCard({}, actionsData));
            const activity = await api.getRequestActivity(state.request.id).catch(() => null);
            if (activity) commit('setRequestActivity', activity.items || []);
            return { success: true };
        } catch (error) {
            console.error('Ошибка при изменении срока заявки:', error);
            throw error;
        } finally {
            commit('setSavingRequest', false);
        }
    },

    async requestCard_fetchBuildingProcessOptions({ state }) {
        if (!state.request || !state.request.id) {
            return [];
        }

        return api.getBuildingProcessOptions(state.request.id);
    },

    requestCard_setBuildingProcess({ state, getters, commit }, option) {
        if (!getters.canEditRequestObject || state.savingRequest || state.submittingRequest) return;
        commit('setBuildingProcessDraft', option);
    },

    /**
     * Отправить заявку на согласование
     */
    async requestCard_submitRequest({ state, commit, dispatch, getters }, { comment, deadlineCorrection } = {}) {
        if (state.submittingRequest) return {success: false};
        if (state.uploadingRequiredAttachment) {
            throw new Error('Дождитесь загрузки обязательного документа');
        }
        commit('setSubmittingRequest', true);
        try {
            if (getters.hasFormChanges) {
                const saved = await dispatch('requestCard_saveRequest');
                if (saved && saved.lostAccess) return {...saved, submitted: false};
                if (saved && saved.refreshFailed) throw new Error(saved.message + ' Отправка не выполнена.');
            }

            if (deadlineCorrection) await dispatch('requestCard_updateDeadline', deadlineCorrection);
            try {
                await api.submitRequest(state.request.id, comment, state.originalRequest && state.originalRequest.updatedAt);
            } catch (error) {
                if (deadlineCorrection) error.deadlineSaved = true;
                error.submissionUncertain = !error.response;
                throw error;
            }
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при отправке заявки:', error);
            throw error;
        } finally {
            commit('setSubmittingRequest', false);
        }
    },

    /**
     * Согласовать заявку
     */
    async requestCard_approveRequest({ state, commit, dispatch, getters }, { comment = null } = {}) {
        assertNoPendingRequestChanges(state, getters);
        commit('setApprovingRequest', true);
        try {
            await api.approveRequest(state.request.id, comment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при согласовании заявки:', error);
            throw error;
        } finally {
            commit('setApprovingRequest', false);
        }
    },

    /**
     * Отклонить заявку
     */
    async requestCard_rejectRequest({ state, commit, dispatch, getters }, { comment }) {
        assertNoPendingRequestChanges(state, getters);
        commit('setRejectingRequest', true);
        try {
            await api.rejectRequest(state.request.id, comment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при отклонении заявки:', error);
            throw error;
        } finally {
            commit('setRejectingRequest', false);
        }
    },

    /**
     * Взять заявку в работу
     */
    async requestCard_executeRequest({ state, commit, dispatch, getters }, { comment } = {}) {
        assertNoPendingRequestChanges(state, getters);
        commit('setExecutingRequest', true);
        try {
            await api.executeRequest(state.request.id, comment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при взятии заявки в работу:', error);
            throw error;
        } finally {
            commit('setExecutingRequest', false);
        }
    },

    /**
     * Вернуть заявку на доработку
     */
    async requestCard_returnRequest({ state, commit, dispatch, getters }, { comment }) {
        assertNoPendingRequestChanges(state, getters);
        commit('setReturningRequest', true);
        try {
            await api.returnRequest(state.request.id, comment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при возврате заявки:', error);
            throw error;
        } finally {
            commit('setReturningRequest', false);
        }
    },

    /**
     * Завершить выполнение заявки (на приемку)
     */
    async requestCard_completeRequest({ state, commit, dispatch, getters }, { comment, fulfillment } = {}) {
        assertNoPendingRequestChanges(state, getters);
        commit('setCompletingRequest', true);
        try {
            await api.completeRequest(state.request.id, comment, fulfillment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при завершении заявки:', error);
            throw error;
        } finally {
            commit('setCompletingRequest', false);
        }
    },

    /**
     * Частично выполнить заявку
     */
    async requestCard_partiallyCompleteRequest({ state, commit, dispatch, getters }, { comment, fulfillment } = {}) {
        assertNoPendingRequestChanges(state, getters);
        commit('setPartiallyCompletingRequest', true);
        try {
            await api.partiallyCompleteRequest(state.request.id, comment, fulfillment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при частичном выполнении заявки:', error);
            throw error;
        } finally {
            commit('setPartiallyCompletingRequest', false);
        }
    },

    /**
     * Принять выполненную заявку
     */
    async requestCard_acceptRequest({ state, commit, dispatch, getters }, { comment, fulfillment } = {}) {
        assertNoPendingRequestChanges(state, getters);
        commit('setAcceptingRequest', true);
        try {
            await api.acceptRequest(state.request.id, comment, fulfillment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при принятии заявки:', error);
            throw error;
        } finally {
            commit('setAcceptingRequest', false);
        }
    },

    /**
     * Принять частично выполненную заявку
     */
    async requestCard_acceptPartiallyRequest({ state, commit, dispatch, getters }, { comment, fulfillment } = {}) {
        assertNoPendingRequestChanges(state, getters);
        commit('setAcceptingPartiallyRequest', true);
        try {
            await api.acceptPartiallyRequest(state.request.id, comment, fulfillment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при частичном принятии заявки:', error);
            throw error;
        } finally {
            commit('setAcceptingPartiallyRequest', false);
        }
    },

    /**
     * Отменить заявку
     */
    async requestCard_cancelRequest({ state, commit, dispatch, getters }, { comment } = {}) {
        assertNoPendingRequestChanges(state, getters);
        commit('setCancellingRequest', true);
        try {
            await api.cancelRequest(state.request.id, comment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при отмене заявки:', error);
            throw error;
        } finally {
            commit('setCancellingRequest', false);
        }
    },

    /**
     * Изменить статус заявки напрямую (только для администратора)
     */
    async requestCard_changeRequestStatus({ state, commit, dispatch, getters }, { targetStatus, comment, closeWithoutMechanicActions = false }) {
        assertNoPendingRequestChanges(state, getters);
        commit('setChangingStatus', true);
        try {
            await api.changeRequestStatus(state.request.id, targetStatus, comment, closeWithoutMechanicActions);
            await dispatch('requestCard_fetchRequest', state.request.id);
            
            // Обновляем счетчики заявок
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            
            return { success: true };
        } catch (error) {
            console.error('Ошибка при изменении статуса заявки:', error);
            throw error;
        } finally {
            commit('setChangingStatus', false);
        }
    },

    async requestCard_uploadRequiredAttachment({ state, commit }, { fileKind, files, onUploaded }) {
        const uploadFiles = toFileArray(files);
        if (!state.request || !state.request.id || !fileKind || !uploadFiles.length) {
            return null;
        }

        if (state.uploadingRequiredAttachment || state.savingRequest || state.submittingRequest) {
            throw new Error('Дождитесь завершения текущего действия');
        }
        const requestId = state.request.id;
        commit('setUploadingRequiredAttachment', true);
        try {
            const results = [];
            for (const file of uploadFiles) {
                const result = await api.uploadRequiredAttachment(requestId, fileKind, [file]);
                // Полная перезагрузка сбрасывает удаления и остальные несохранённые правки.
                commit('mergeUploadedRequestFiles', { requestId, files: result.files });
                if (onUploaded) onUploaded(file);
                results.push(result);
            }
            return results;
        } finally {
            commit('setUploadingRequiredAttachment', false);
        }
    },

    /**
     * Назначить исполнителя на заявку
     */
    async requestCard_assignExecutor({ state, commit, dispatch }, { executorId, comment = null }) {
        commit('setAssigningExecutor', true);
        try {
            await api.assignRequest(state.request.id, executorId, comment);
            await dispatch('requestCard_fetchRequest', state.request.id);
            commit('setSelectedExecutorId', null);
            return { success: true };
        } catch (error) {
            console.error('Ошибка при назначении исполнителя:', error);
            throw error;
        } finally {
            commit('setAssigningExecutor', false);
        }
    },

    /**
     * Получить сотрудников для замены согласующего
     */
    async requestCard_getApproverReplacementOptions({ state }, { participantId, godMode = false }) {
        if (!state.request || !state.request.id || !participantId) {
            return { users: [] };
        }

        return api.getApproverReplacementOptions(state.request.id, participantId, godMode);
    },

    /**
     * Получить сотрудников для добавления согласующего
     */
    async requestCard_getApproverAddOptions({ state }, { godMode = false } = {}) {
        if (!state.request || !state.request.id) {
            return { users: [] };
        }

        return api.getApproverAddOptions(state.request.id, godMode);
    },

    /**
     * Заменить согласующего в конкретной заявке
     */
    async requestCard_replaceApprover({ state, dispatch }, { participantId, newUserId, reason, suppressNotification, godMode = false }) {
        if (!state.request || !state.request.id) {
            return;
        }

        await api.replaceApprover(state.request.id, participantId, newUserId, reason, suppressNotification, godMode);
        await refreshRequestAndCounts({ state, dispatch });
        return { success: true };
    },

    /**
     * Удалить согласующего из конкретной заявки
     */
    async requestCard_removeApprover({ state, dispatch }, { participantId, reason, suppressNotification, godMode = false }) {
        if (!state.request || !state.request.id) {
            return;
        }

        await api.removeApprover(state.request.id, participantId, reason, suppressNotification, godMode);
        await refreshRequestAndCounts({ state, dispatch });
        return { success: true };
    },

    /**
     * Добавить согласующего в конкретную заявку
     */
    async requestCard_addApprover({ state, dispatch }, { newUserId, reason, suppressNotification, godMode = false }) {
        if (!state.request || !state.request.id) {
            return;
        }

        await api.addApprover(state.request.id, newUserId, reason, suppressNotification, godMode);
        await refreshRequestAndCounts({ state, dispatch });
        return { success: true };
    },

    /**
     * Получить сотрудников для замены участника процесса
     */
    async requestCard_getParticipantReplacementOptions({ state }, { participantId }) {
        if (!state.request || !state.request.id || !participantId) {
            return { users: [] };
        }

        return api.getParticipantReplacementOptions(state.request.id, participantId);
    },

    /**
     * Получить сотрудников для добавления участника процесса
     */
    async requestCard_getParticipantAddOptions({ state }, { role }) {
        if (!state.request || !state.request.id || !role) {
            return { users: [] };
        }

        return api.getParticipantAddOptions(state.request.id, role);
    },

    /**
     * Заменить участника процесса в конкретной заявке
     */
    async requestCard_replaceParticipant({ state, dispatch }, { participantId, newUserId, reason, suppressNotification }) {
        if (!state.request || !state.request.id) {
            return;
        }

        await api.replaceParticipant(state.request.id, participantId, newUserId, reason, suppressNotification);
        await refreshRequestAndCounts({ state, dispatch });
        return { success: true };
    },

    /**
     * Удалить участника процесса из конкретной заявке
     */
    async requestCard_removeParticipant({ state, dispatch }, { participantId, reason, suppressNotification }) {
        if (!state.request || !state.request.id) {
            return;
        }

        await api.removeParticipant(state.request.id, participantId, reason, suppressNotification);
        await refreshRequestAndCounts({ state, dispatch });
        return { success: true };
    },

    /**
     * Добавить участника процесса в конкретную заявку
     */
    async requestCard_addParticipant({ state, dispatch }, { role, newUserId, reason, suppressNotification }) {
        if (!state.request || !state.request.id) {
            return;
        }

        await api.addParticipant(state.request.id, role, newUserId, reason, suppressNotification);
        await refreshRequestAndCounts({ state, dispatch });
        return { success: true };
    },

    /**
     * Включить режим «несколько исполнителей» и распределить материалы
     * @param {{assignments: Array<{item_id:number,user_id:number}>}} payload
     */
    async requestCard_assignMultiExecutors({ state, commit, dispatch }, { assignments }) {
        if (!state.request || !state.request.id) {
            return;
        }
        commit('setAssigningExecutor', true);
        try {
            await api.assignMultiExecutors(state.request.id, assignments);
            await dispatch('requestCard_fetchRequest', state.request.id);
            try {
                await dispatch('requests_getCounts', null, { root: true });
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            return { success: true };
        } catch (error) {
            console.error('Ошибка включения multi-режима:', error);
            throw error;
        } finally {
            commit('setAssigningExecutor', false);
        }
    },

    /**
     * Переназначить исполнителя одной строки материала (multi-режим)
     */
    async requestCard_reassignItemExecutor({ state, dispatch, commit }, { itemId, executorId }) {
        if (!state.request || !state.request.id) {
            return;
        }
        commit('setAssigningExecutor', true);
        try {
            await api.reassignItemExecutor(state.request.id, itemId, executorId);
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка переназначения исполнителя строки:', error);
            throw error;
        } finally {
            commit('setAssigningExecutor', false);
        }
    },

    /**
     * Массово переназначить исполнителя нескольких строк материала (multi-режим)
     */
    async requestCard_bulkReassignItemExecutor({ state, dispatch, commit }, { itemIds, executorId }) {
        if (!state.request || !state.request.id) {
            return;
        }
        commit('setAssigningExecutor', true);
        try {
            await api.bulkReassignItemExecutor(state.request.id, itemIds, executorId);
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка массового переназначения исполнителя строк:', error);
            throw error;
        } finally {
            commit('setAssigningExecutor', false);
        }
    },

    async requestCard_selfAssignItemExecutor({ state, dispatch, commit }, { itemId }) {
        if (!state.request || !state.request.id) {
            return;
        }
        commit('setAssigningExecutor', true);
        try {
            await api.selfAssignItemExecutor(state.request.id, itemId);
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка самоназначения строки:', error);
            throw error;
        } finally {
            commit('setAssigningExecutor', false);
        }
    },

    async requestCard_selfReleaseItemExecutor({ state, dispatch, commit }, { itemId }) {
        if (!state.request || !state.request.id) {
            return;
        }
        commit('setAssigningExecutor', true);
        try {
            await api.selfReleaseItemExecutor(state.request.id, itemId);
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка снятия самоназначения строки:', error);
            throw error;
        } finally {
            commit('setAssigningExecutor', false);
        }
    },

    async requestCard_selfAssignBulk({ state, dispatch, commit }) {
        if (!state.request || !state.request.id) {
            return;
        }
        commit('setAssigningExecutor', true);
        try {
            await api.selfAssignBulk(state.request.id);
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка массового самоназначения строк:', error);
            throw error;
        } finally {
            commit('setAssigningExecutor', false);
        }
    },

    async requestCard_receiveMechanicAsset({ state }, { itemId, payload }) {
        if (!state.request || !state.request.id || !itemId) {
            return null;
        }

        return api.receiveMechanicAsset(state.request.id, itemId, payload || {});
    },

    async requestCard_getFuelCardOptions({ state }) {
        if (!state.request || !state.request.id) {
            return [];
        }

        return api.getFuelCardOptions(state.request.id);
    },

    async requestCard_selectFuelCardForItem({ state, dispatch }, { itemId, fuelCardId }) {
        if (!state.request || !state.request.id || !itemId || !fuelCardId) {
            return null;
        }

        await api.selectFuelCardForItem(state.request.id, itemId, fuelCardId);
        await dispatch('requestCard_fetchRequest', state.request.id);
        return { success: true };
    },

    async requestCard_getFuelCardTargetObjectOptions({ state }) {
        if (!state.request || !state.request.id) {
            return [];
        }

        return api.getFuelCardTargetObjectOptions(state.request.id);
    },

    async requestCard_selectFuelCardTargetObjectForItem({ state, dispatch }, { itemId, buildingProcessId }) {
        if (!state.request || !state.request.id || !itemId || !buildingProcessId) {
            return null;
        }

        await api.selectFuelCardTargetObjectForItem(state.request.id, itemId, buildingProcessId);
        await dispatch('requestCard_fetchRequest', state.request.id);
        return { success: true };
    },

    async requestCard_updateFuelCardLimitsForItem({ state, dispatch }, { itemId, fuelLimits }) {
        if (!state.request || !state.request.id || !itemId) {
            return null;
        }

        await api.updateFuelCardLimitsForItem(state.request.id, itemId, fuelLimits || []);
        await dispatch('requestCard_fetchRequest', state.request.id);
        return { success: true };
    },

    /**
     * Скачать список позиций заявки в Excel
     */
    async requestCard_downloadMaterialsList({ state, commit }) {
        if (!state.request || !state.request.id) {
            return;
        }

        commit('setDownloadingMaterials', true);
        try {
            const response = await api.exportMaterials(state.request.id);

            // Создаем ссылку для скачивания
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Заявка_${state.request.request_number || state.request.id}_позиции.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            console.error('Ошибка при скачивании списка позиций заявки:', error);
            throw error;
        } finally {
            commit('setDownloadingMaterials', false);
        }
    },

    /**
     * Добавить комментарий к заявке
     */
    async requestCard_addComment({ state, dispatch }, { text, files, draft = {} }) {
        try {
            const commentFiles = toFileArray(files);
            await submitCommentFiles(draft, text, commentFiles, {
                create: value => api.addComment(state.request.id, value),
                update: (id, value) => api.updateComment(state.request.id, id, value),
                upload: (id, file) => api.addCommentFile(state.request.id, id, file)
            });
            
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка при добавлении комментария:', error);
            throw error;
        }
    },

    /**
     * Обновить комментарий
     */
    async requestCard_updateComment({ state, dispatch }, payload = {}) {
        try {
            const rawCommentId = payload.commentId !== undefined
                ? payload.commentId
                : payload.id;
            const commentId = Number(rawCommentId);
            if (!Number.isInteger(commentId) || commentId <= 0) {
                throw new Error('Некорректный идентификатор комментария');
            }
            const text = payload.text;
            await api.updateComment(state.request.id, commentId, text);
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка при обновлении комментария:', error);
            throw error;
        }
    },

    /**
     * Удалить комментарий
     */
    async requestCard_deleteComment({ state, dispatch }, commentId) {
        try {
            await api.deleteComment(state.request.id, commentId);
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка при удалении комментария:', error);
            throw error;
        }
    },

    /**
     * Удалить файл из комментария
     */
    async requestCard_deleteCommentFile({ state, dispatch }, { commentId, fileId }) {
        try {
            await api.deleteCommentFile(state.request.id, commentId, fileId);
            await dispatch('requestCard_fetchRequest', state.request.id);
            return { success: true };
        } catch (error) {
            console.error('Ошибка при удалении файла комментария:', error);
            throw error;
        }
    },

    /**
     * Автосохранение черновика
     */
    async requestCard_autoSaveDraft({ state, commit, getters }) {
        if (!getters.canEditRequestDetails || !getters.hasFormChanges || state.savingRequest || !state.request || !state.request.id) {
            return;
        }

        try {
            const draftData = {
                request_type_id: state.request.request_type_id,
                title: state.request.title || '',
                description: state.request.description || '',
                priority: state.request.priority || 'normal',
                deadline: state.request.deadline || null,
                urgency_comment: state.request.urgency_comment || '',
                building_process_id: state.request.building_process_id || null,
                custom_fields: getCustomFieldValues(state.request),
                materialsChanges: getters.materialsListHaveChanges ? {
                    added: (state.materialsChanges.added || []).map(item => ({
                        item_name: item.item_name || '',
                        quantity: item.quantity || 0,
                        unit: item.unit || '',
                        isCustom: item.isCustom || false
                    })),
                    updated: (state.materialsChanges.updated || []).map(item => ({
                        id: item.id,
                        item_name: item.item_name || '',
                        quantity: item.quantity || 0,
                        unit: item.unit || ''
                    })),
                    deleted: (state.materialsChanges.deleted || []).map(item => ({
                        id: item.id,
                        _delete: true
                    }))
                } : null,
                newAttachments: (state.newAttachments || []).map(file => ({
                    name: file.name,
                    size: file.size,
                    type: file.type || ''
                })),
                lastSavedAt: new Date().toISOString()
            };

            const previous = pendingDraftSaves.get(state);
            const saving = (previous || Promise.resolve()).catch(() => {}).then(() => api.saveDraft(draftData.request_type_id, draftData));
            pendingDraftSaves.set(state, saving);
            try { await saving; }
            finally { if (pendingDraftSaves.get(state) === saving) pendingDraftSaves.delete(state); }
            commit('setLastAutoSaveTime', new Date());

            return { success: true };
        } catch (error) {
            console.error('Ошибка автосохранения черновика:', error);
            // Не пробрасываем ошибку, чтобы не мешать работе
        }
    },

    /**
     * Обработать изменения файлов
     */
    requestCard_handleAttachmentsChange({ commit }, files) {
        const incoming = Array.isArray(files)
            ? files
            : (files && typeof files.length === 'number' ? Array.from(files) : files ? [files] : []);
        
        if (!incoming.length) {
            commit('setAttachmentsInputValue', null);
            return;
        }

        commit('addNewAttachments', incoming);
        commit('setAttachmentsInputValue', null);
    },

    /**
     * Заменить список новых файлов заявки целиком
     */
    requestCard_setNewAttachments({ commit }, files) {
        const incoming = Array.isArray(files)
            ? files
            : (files && typeof files.length === 'number' ? Array.from(files) : files ? [files] : []);
        commit('setNewAttachments', incoming);
        commit('setAttachmentsInputValue', null);
    },

    /**
     * Удалить новый файл
     */
    requestCard_removeNewAttachment({ commit }, index) {
        commit('removeNewAttachment', index);
    },

    /**
     * Удалить файл заявки
     */
    requestCard_removeRequestFile({ commit }, fileId) {
        commit('removeRequestFile', fileId);
    },

    /**
     * Обработать изменения материалов
     */
    requestCard_handleMaterialsChange({ commit }, changes) {
        commit('setMaterialsChanges', changes);
    },

    /**
     * Начать редактирование списка материалов
     */
    requestCard_startEditingMaterialsList({ commit }) {
        commit('setEditingMaterialsList', true);
    },

    /**
     * Отменить редактирование списка материалов
     */
    requestCard_cancelEditingMaterialsList({ commit }) {
        commit('clearMaterialsChanges');
    },

    /**
     * Обновить поле заявки
     */
    requestCard_updateField({ commit }, { field, value }) {
        commit('updateRequestField', { field, value });
    },

    /**
     * Сбросить состояние модуля
     */
    requestCard_reset({ commit, state }) {
        // Очистка таймера автосохранения перед сбросом
        if (state.autoSaveTimer) {
            clearTimeout(state.autoSaveTimer);
        }
        commit('resetState');
    }
};
import {submitCommentFiles} from '@/utils/commentSubmission';
